import { X } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn, twRing, twTransform } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { WarningAlertIcon, warningSurfaceStyle } from "@/components/ui/feedback/warningVisual";
import { ToastContext } from "./Toast.context";
import type { ToastContextValue, ToastInput, ToastManagerProps, ToastPlacement, ToastProviderProps, ToastRecord, ToastTone } from "./Toast.types";

/* Exact compiled output of the pre-migration Tailwind utilities. */

const toastPlacementStyles: Record<ToastPlacement, CSSObject> = {
  "top-left": { left: "1rem", top: "1rem", alignItems: "flex-start" },
  "top-center": { left: "50%", top: "1rem", ...twTransform({ "--tw-translate-x": "-50%" }), alignItems: "center" },
  "top-right": { right: "1rem", top: "1rem", alignItems: "flex-end" },
  "bottom-left": { bottom: "1rem", left: "1rem", alignItems: "flex-start" },
  "bottom-center": { bottom: "1.25rem", left: "50%", ...twTransform({ "--tw-translate-x": "-50%" }), alignItems: "center" },
  "bottom-right": { bottom: "1rem", right: "1rem", alignItems: "flex-end" }
};

const borderL = (token: string): CSSObject => ({
  "--tw-border-opacity": "1",
  borderLeftColor: `hsl(var(--bfrs-color-${token}) / var(--tw-border-opacity, 1))`
});

const toastToneBorderStyles: Record<ToastTone, CSSObject> = {
  neutral: borderL("primary"),
  success: borderL("success"),
  warning: {},
  danger: borderL("danger"),
  info: borderL("info")
};

const bgTone = (token: string): CSSObject => ({
  "--tw-bg-opacity": "1",
  backgroundColor: `hsl(var(--bfrs-color-${token}) / var(--tw-bg-opacity, 1))`
});

const toastToneIconStyles: Record<ToastTone, CSSObject> = {
  neutral: bgTone("primary"),
  success: bgTone("success"),
  warning: {},
  danger: bgTone("danger"),
  info: bgTone("info")
};

const ToastIconBubble = styled("span", {
  label: "AgenticToast-iconBubble",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { tone: ToastTone; plain?: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    width: "1.25rem",
    height: "1.25rem",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-pill)"
  },
  !ownerState.plain && {
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: 1,
    "--tw-text-opacity": "1",
    color: "rgb(255 255 255 / var(--tw-text-opacity, 1))"
  },
  !ownerState.plain && toastToneIconStyles[ownerState.tone]
]);

const ToastItemRoot = styled("div", {
  label: "AgenticToast-item",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { tone: ToastTone } }>(({ ownerState }) => [
  {
    pointerEvents: "auto",
    display: "flex",
    width: "100%",
    minWidth: "300px",
    maxWidth: "400px",
    alignItems: "flex-start",
    gap: ".625rem",
    borderRadius: "10px",
    borderWidth: "1px",
    borderLeftWidth: "3px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    paddingLeft: "14px",
    paddingRight: "14px",
    paddingTop: ".75rem",
    paddingBottom: ".75rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    ...kfContentIn,
    animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  toastToneBorderStyles[ownerState.tone]
]);

const ToastContent = styled("div", {
  label: "AgenticToast-content"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const ToastTitle = styled("div", {
  label: "AgenticToast-title"
})({
  fontSize: "13px",
  fontWeight: 600,
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const ToastDescription = styled("div", {
  label: "AgenticToast-description"
})({
  marginTop: ".125rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const ToastAction = styled("div", {
  label: "AgenticToast-action"
})({
  marginLeft: ".25rem",
  flexShrink: 0
});

const ToastDismissButton = styled("button", {
  label: "AgenticToast-dismissButton"
})({
  marginRight: "-.25rem",
  display: "flex",
  width: "1.5rem",
  height: "1.5rem",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-md)",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .5)")
});

const ToastStack = styled("div", {
  label: "AgenticToast-stack",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { placement: ToastPlacement } }>(({ ownerState }) => [
  {
    pointerEvents: "none",
    position: "fixed",
    zIndex: 2147483646,
    display: "flex",
    width: "calc(100vw - 2rem)",
    maxWidth: "400px",
    flexDirection: "column",
    gap: ".625rem"
  },
  toastPlacementStyles[ownerState.placement]
]);

const toneIconGlyph: Record<ToastTone, string> = {
  neutral: "i",
  success: "✓",
  warning: "",
  danger: "!",
  info: "i"
};

function normalizeToast(input: ToastInput, defaultDuration: number): Omit<ToastRecord, "id"> & { id?: string } {
  if (typeof input === "string") {
    return {
      title: input,
      tone: "neutral",
      duration: defaultDuration,
      dismissible: true
    };
  }

  return {
    tone: "neutral",
    duration: defaultDuration,
    dismissible: true,
    ...input
  };
}

function withTone(tone: ToastTone, input: string | Omit<Extract<ToastInput, object>, "tone">): ToastInput {
  return typeof input === "string" ? { title: input, tone } : { ...input, tone };
}

function DefaultToastIcon({ tone }: { tone: ToastTone }) {
  if (tone === "warning") {
    return (
      <ToastIconBubble ownerState={{ tone, plain: true }} style={warningSurfaceStyle} aria-hidden="true">
        <WarningAlertIcon />
      </ToastIconBubble>
    );
  }

  return (
    <ToastIconBubble ownerState={{ tone }} aria-hidden="true">
      {toneIconGlyph[tone]}
    </ToastIconBubble>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: string) => void }) {
  useEffect(() => {
    if (toast.duration <= 0 || !Number.isFinite(toast.duration)) return undefined;

    const timeout = window.setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast.duration, toast.id]);

  const role = toast.tone === "danger" ? "alert" : "status";

  return (
    <ToastItemRoot
      role={role}
      aria-live={role === "alert" ? "assertive" : "polite"}
      ownerState={{ tone: toast.tone }}
      className={toast.className}
      style={toast.tone === "warning" ? warningSurfaceStyle : undefined}
    >
      {toast.icon ?? <DefaultToastIcon tone={toast.tone} />}
      <ToastContent>
        <ToastTitle>{toast.title}</ToastTitle>
        {toast.description && <ToastDescription>{toast.description}</ToastDescription>}
      </ToastContent>
      {toast.action && <ToastAction>{toast.action}</ToastAction>}
      {toast.dismissible && (
        <ToastDismissButton type="button" onClick={() => onDismiss(toast.id)} aria-label="Dismiss notification">
          <X size={13} weight="bold" />
        </ToastDismissButton>
      )}
    </ToastItemRoot>
  );
}

export function ToastManager({ toasts, onDismiss, placement = "bottom-center", className }: ToastManagerProps) {
  if (toasts.length === 0) return null;

  const stack = (
    // Portaled to <body> so the fixed, z-[9999] stack escapes any ancestor
    // stacking context (a transformed/opacity/filtered parent would otherwise
    // trap it and let page content paint over the toasts). BfrsPortalScope
    // re-applies the .bfrs token scope at the portal root.
    <BfrsPortalScope>
      <ToastStack ownerState={{ placement }} className={className}>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </ToastStack>
    </BfrsPortalScope>
  );

  if (typeof document === "undefined") return stack;
  return createPortal(stack, document.body);
}

export function ToastProvider({ children, placement = "bottom-center", maxToasts = 4, defaultDuration = 2600, className }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const idCounterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const clear = useCallback(() => {
    setToasts([]);
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      const normalized = normalizeToast(input, defaultDuration);
      const id = normalized.id ?? `toast-${Date.now()}-${idCounterRef.current++}`;
      const nextToast: ToastRecord = { ...normalized, id };

      setToasts((current) => [...current.filter((item) => item.id !== id), nextToast].slice(-maxToasts));
      return id;
    },
    [defaultDuration, maxToasts]
  );

  const success = useCallback((input: string | Omit<Extract<ToastInput, object>, "tone">) => toast(withTone("success", input)), [toast]);
  const warning = useCallback((input: string | Omit<Extract<ToastInput, object>, "tone">) => toast(withTone("warning", input)), [toast]);
  const danger = useCallback((input: string | Omit<Extract<ToastInput, object>, "tone">) => toast(withTone("danger", input)), [toast]);
  const info = useCallback((input: string | Omit<Extract<ToastInput, object>, "tone">) => toast(withTone("info", input)), [toast]);

  const contextValue = useMemo<ToastContextValue>(
    () => ({ toasts, toast, success, warning, danger, info, dismiss, clear }),
    [clear, danger, dismiss, info, success, toast, toasts, warning]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastManager toasts={toasts} onDismiss={dismiss} placement={placement} className={className} />
    </ToastContext.Provider>
  );
}
